import { useInfiniteQuery } from "@tanstack/react-query";
import { customAxios } from "lib/axios";
import storage from "utils/storage";
import jwt_decode, { JwtPayload } from "jwt-decode";

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: string;
  avatar: string;
  userName: string;
  isRead?: boolean;
  hasIndicator?: boolean;
  priority?: string;
  project?: string;
  projectId?: {
    _id: string;
    name: string;
  };
  issueId?: string;
  color?: string;
  createdAt?: string;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface NotificationResponse {
  notifications: NotificationItem[];
  pagination: Pagination;
}

type Payload = JwtPayload & {
  _id: string;
};

const getNotifications = async (
  page: number,
  signal: AbortSignal | undefined
): Promise<NotificationResponse> => {
  const token = storage.getToken();
  if (!token) {
    return {
      notifications: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
        hasNextPage: false,
        hasPrevPage: false,
      },
    };
  }

  try {
    const decoded = jwt_decode<Payload>(token);
    const userId = decoded._id;

    const { data } = await customAxios.get<NotificationResponse>(
      `/notifications/${userId}?page=${page}&limit=10`,
      { signal }
    );

    return {
      notifications: data.notifications,
      pagination: data.pagination,
    };
  } catch (error) {
    if (error instanceof Error && error.message.includes("Invalid token")) {
      return {
        notifications: [],
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          limit: 10,
          hasNextPage: false,
          hasPrevPage: false,
        },
      };
    }
    throw error;
  }
};

export const useGetNotifications = () =>
  useInfiniteQuery<NotificationResponse, Error>(
    ["notifications"],
    ({ pageParam = 1, signal }) =>
      getNotifications(pageParam as number, signal),
    {
      getNextPageParam: (lastPage) =>
        lastPage.pagination.hasNextPage
          ? lastPage.pagination.currentPage + 1
          : undefined,
      enabled: !!storage.getToken(),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        if (error instanceof Error && error.message.includes("401")) {
          return false;
        }
        return failureCount < 3;
      },
    }
  );
